/**
 * Impersonation session helpers (frontend-only).
 *
 * When an admin impersonates another user via `login-admin-user/{id}` the app
 * fully swaps the session in localStorage, so nothing remembers who the real
 * admin is. These helpers stash the original session before the swap so that:
 *  - a global banner can show "X is viewing as Y" while impersonating, and
 *  - "Exit impersonation" can restore the original session without re-login.
 *
 * The stash lives in localStorage under IMPERSONATION_KEY. The store getters
 * fall back to localStorage (src/store/modules/auth.js), so restoring the keys
 * and doing a full page load rehydrates the original session.
 */

const IMPERSONATION_KEY = 'impersonation';
const SESSION_KEYS = ['user', 'role', 'company', 'access_token', 'refresh_token'];

export interface ImpersonationStash {
  by: { name: string; email: string; role: string };
  session: Record<string, string | null>;
  started_at: string;
}

const safeParse = (value: string | null): Record<string, any> => {
  try {
    return JSON.parse(value || 'null') || {};
  } catch (e) {
    return {};
  }
};

/**
 * Capture the CURRENT session (the real admin) before it is overwritten.
 * Call this BEFORE handleUserLoginLogOutAction('logout') and save the result
 * AFTER the impersonated session has been written (logout clears the key).
 * If an impersonation stash already exists (impersonating from an impersonated
 * session), the ORIGINAL stash is returned so the return point is preserved.
 */
export const captureImpersonationStash = (): ImpersonationStash | null => {
  const existing = getImpersonation();
  if (existing) {
    return existing;
  }
  if (!localStorage.getItem('access_token')) {
    return null;
  }
  const session: Record<string, string | null> = {};
  SESSION_KEYS.forEach((key) => {
    session[key] = localStorage.getItem(key);
  });
  const user = safeParse(localStorage.getItem('user'));
  const role = safeParse(localStorage.getItem('role'));
  return {
    by: {
      name: user.name || '',
      email: user.email || '',
      role: role.name || '',
    },
    session,
    started_at: new Date().toISOString(),
  };
};

export const saveImpersonationStash = (stash: ImpersonationStash | null): void => {
  if (stash) {
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(stash));
  }
};

export const getImpersonation = (): ImpersonationStash | null => {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_KEY) || 'null');
  } catch (e) {
    return null;
  }
};

export const clearImpersonation = (): void => {
  localStorage.removeItem(IMPERSONATION_KEY);
};

/**
 * Restore the original (stashed) session and reload the app as that user.
 */
export const exitImpersonation = (): void => {
  const stash = getImpersonation();
  if (stash && stash.session && stash.session.access_token) {
    SESSION_KEYS.forEach((key) => {
      const value = stash.session[key];
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    });
    // Clear the stash only AFTER the restore completed, so a mid-loop
    // storage failure (quota, private browsing) cannot strand a partially
    // restored session with no stash left to retry from.
    clearImpersonation();
    // Full page load so the store rehydrates from localStorage and
    // permissions are refetched for the restored user.
    window.location.href = '/';
    return;
  }
  // No valid stash to restore: fail SAFE by ending the impersonated session
  // entirely instead of silently leaving the admin inside it with no banner.
  // The user lands on the login page with a clean, well-defined state.
  clearImpersonation();
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  window.location.href = '/login';
};
